import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEllipsisV, FaFilter, FaPaperPlane, FaPlusCircle, FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import AdminModalShell from "./AdminModalShell";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import AdminMobileViewToggle from "./AdminMobileViewToggle";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";
import { recordPayrollPayment } from "../utils/teacherPayrollLedger";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";


const defaultForm = {
  id: "",
  name: "",
  email: "",
  subject: "",
  qualification: "",
  experience: "",
  contact: "",
  assignedClass: "",
  assignedSection: "",
  monthlySalary: "",
};

const defaultPaymentForm = {
  method: "BANK_TRANSFER",
  amount: "",
  paidOn: new Date().toISOString().slice(0, 10),
  referenceId: "",
  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  upiId: "",
  notes: "",
};

const optionId = (record = {}) => record.id || record._id || record.classId || record.subjectId || record.sectionId || "";
const optionLabel = (record) => typeof record === "string" ? record : (record?.name || record?.label || record?.className || record?.subjectName || record?.section || record?.sectionName || "");
const extractOptionList = (responseData, key) => {
  const payload = responseData?.data ?? responseData ?? [];
  return Array.isArray(payload) ? payload : (Array.isArray(payload?.[key]) ? payload[key] : (Array.isArray(payload?.content) ? payload.content : []));
};

const normalizeTeacher = (teacher = {}, index = 0) => ({
  id: teacher.id || teacher.teacherId || teacher._id || `teacher-${index + 1}`,
  employeeId: teacher.employeeId || teacher.employeeCode || teacher.staffId || "—",
  name: teacher.name || teacher.fullName || teacher.teacherName || "Unnamed Teacher",
  email: teacher.email || "",
  subject: teacher.subject || teacher.subjectAssignments?.[0]?.subjectName || teacher.subjectAssignments?.[0]?.subject || teacher.assignments?.[0]?.subjectName || teacher.assignments?.[0]?.subject || teacher.department || "Not assigned",
  department: teacher.department || teacher.subject || "Not assigned",
  qualification: teacher.qualification || "Not updated",
  experience: Number(teacher.experience ?? teacher.yearsOfExperience ?? 0),
  contact: teacher.contact || teacher.phone || teacher.mobile || "Not updated",
  assignedClass: String(teacher.assignedClass || teacher.className || teacher.standard || teacher.subjectAssignments?.[0]?.className || teacher.assignments?.[0]?.className || "-"),
  assignedSection: String(teacher.assignedSection || teacher.section || teacher.subjectAssignments?.[0]?.sectionName || teacher.assignments?.[0]?.sectionName || "-"),
  monthlySalary: Number(teacher.monthlySalary ?? teacher.salary ?? 0),
  lastPayment: teacher.lastPayment || teacher.lastPaidOn || "Not recorded",
  status: String(teacher.status || (teacher.passwordResetRequired ? "PASSWORD_RESET_REQUIRED" : "ACTIVE")).toUpperCase(),
  accountStatus: String(teacher.accountStatus || teacher.status || (teacher.passwordResetRequired ? "PASSWORD_RESET_REQUIRED" : "ACTIVE")).toUpperCase(),
  canResendInvitation: teacher.canResendInvitation === true || String(teacher.canResendInvitation).toLowerCase() === "true",
  employmentType: teacher.employmentType || "Not specified",
  employmentStatus: String(teacher.employmentStatus || teacher.teacherStatus || "ACTIVE").toUpperCase(),
  joiningDate: teacher.joiningDate || teacher.dateOfJoining || teacher.createdAt || "",
  photo: teacher.photo || teacher.profilePhoto || teacher.image || "",
});

const extractTeachers = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.teachers)) return payload.teachers;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const formatSalary = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const readableStatus = (value) => ({
  PASSWORD_RESET_REQUIRED: "Password Setup Required",
  INVITATION_PENDING: "Invitation Pending",
  PENDING: "Invitation Pending",
  ACTIVE: "Active",
  ON_LEAVE: "On Leave",
  SUSPENDED: "Suspended",
  INACTIVE: "Inactive",
  RESIGNED: "Resigned",
  LOCKED: "Locked",
  DISABLED: "Disabled",
}[String(value || "").toUpperCase()] || String(value || "Not specified").replaceAll("_", " "));

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "T";

const AdminTeacherManagement = () => {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filters, setFilters] = useState({ department: "", subject: "", employmentType: "", status: "", accountStatus: "" });
  const [page, setPage] = useState(1);
  const [openActionsFor, setOpenActionsFor] = useState("");
  const [selectedTeacherId, setSelectedTeacherId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [activeView, setActiveView] = useState("activity");
  const [mobileRecordView, setMobileRecordView] = useState("cards");
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [paymentTeacher, setPaymentTeacher] = useState(null);
  const [paymentForm, setPaymentForm] = useState(defaultPaymentForm);
  const [deletingTeacherId, setDeletingTeacherId] = useState("");
  const [resendingInvitationId, setResendingInvitationId] = useState("");
  const [tenantClasses, setTenantClasses] = useState([]);
  const [tenantSubjects, setTenantSubjects] = useState([]);

  const fetchTeachers = async ({ forceRefresh = false } = {}) => {
      setLoading(true);
      setLoadError(false);

      try {
        const response = await getCachedOrFetch(
          getTenantCacheKey("admin:teachers"),
          () => api.get(API_PATHS.admin.teachers()),
          { ttlMs: 120000, forceRefresh }
        );
        const nextTeachers = extractTeachers(response.data).map(normalizeTeacher);
        setTeachers(nextTeachers);
        setSelectedTeacherId(nextTeachers[0]?.id || "");
      } catch {
        setTeachers([]);
        setSelectedTeacherId("");
        setLoadError(true);
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => { fetchTeachers(); }, []);

  useEffect(() => {
    Promise.allSettled([api.get(API_PATHS.admin.classes()), api.get(API_PATHS.admin.subjects())]).then(([classesResponse, subjectsResponse]) => {
      if (classesResponse.status === "fulfilled") {
        setTenantClasses(extractOptionList(classesResponse.value.data, "classes").map((record) => ({
          id: optionId(record),
          name: optionLabel(record),
          sections: (record.sections || []).map((section) => ({ id: optionId(section), name: optionLabel(section) })),
        })).filter((record) => record.name));
      }
      if (subjectsResponse.status === "fulfilled") {
        setTenantSubjects(extractOptionList(subjectsResponse.value.data, "subjects").map((record) => ({ id: optionId(record), name: optionLabel(record) })).filter((record) => record.name));
      }
    });
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), 350);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  const selectedTeacher = useMemo(
    () => teachers.find((teacher) => teacher.id === selectedTeacherId) || null,
    [selectedTeacherId, teachers]
  );
  const selectedTenantClass = useMemo(
    () => tenantClasses.find((classRecord) => classRecord.name === form.assignedClass || classRecord.id === form.assignedClass) || null,
    [form.assignedClass, tenantClasses]
  );

  useEffect(() => {
    if (!selectedTeacher) {
      setForm(defaultForm);
      return;
    }

    setForm({
      id: selectedTeacher.id,
      name: selectedTeacher.name,
      email: selectedTeacher.email,
      subject: selectedTeacher.subject,
      qualification: selectedTeacher.qualification,
      experience: String(selectedTeacher.experience || ""),
      contact: selectedTeacher.contact,
      assignedClass: selectedTeacher.assignedClass === "-" ? "" : selectedTeacher.assignedClass,
      assignedSection: selectedTeacher.assignedSection === "-" ? "" : selectedTeacher.assignedSection,
      monthlySalary: String(selectedTeacher.monthlySalary || ""),
    });
  }, [selectedTeacher]);

  const filteredTeachers = useMemo(() => {
    const query = debouncedSearchTerm.trim().toLowerCase();

    return teachers.filter((teacher) => (
      !query
      || teacher.name.toLowerCase().includes(query)
      || teacher.email.toLowerCase().includes(query)
      || teacher.subject.toLowerCase().includes(query)
      || teacher.contact.toLowerCase().includes(query)
      || String(teacher.id).toLowerCase().includes(query)
      || String(teacher.employeeId).toLowerCase().includes(query)
      || `${teacher.assignedClass}${teacher.assignedSection}`.toLowerCase().includes(query)
    ) && (!filters.department || teacher.department === filters.department)
      && (!filters.subject || teacher.subject === filters.subject)
      && (!filters.employmentType || teacher.employmentType === filters.employmentType)
      && (!filters.status || teacher.employmentStatus === filters.status)
      && (!filters.accountStatus || teacher.accountStatus === filters.accountStatus));
  }, [debouncedSearchTerm, filters, teachers]);

  useEffect(() => { setPage(1); }, [debouncedSearchTerm, filters]);

  const filterOptions = useMemo(() => ({
    departments: [...new Set(teachers.map((teacher) => teacher.department).filter(Boolean))],
    subjects: [...new Set(teachers.map((teacher) => teacher.subject).filter(Boolean))],
    employmentTypes: [...new Set(teachers.map((teacher) => teacher.employmentType).filter((value) => value && value !== "Not specified"))],
  }), [teachers]);

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredTeachers.length / pageSize));
  const paginatedTeachers = filteredTeachers.slice((page - 1) * pageSize, page * pageSize);

  const payrollSummary = useMemo(() => ({
    totalTeachers: teachers.length,
    totalPayroll: teachers.reduce((sum, teacher) => sum + (teacher.monthlySalary || 0), 0),
    averagePayroll: teachers.length > 0
      ? Math.round(teachers.reduce((sum, teacher) => sum + (teacher.monthlySalary || 0), 0) / teachers.length)
      : 0,
    pendingResets: teachers.filter((teacher) => teacher.status === "PASSWORD_RESET_REQUIRED").length,
  }), [teachers]);

  const handleFormChange = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openCreateForm = () => {
    navigate("/admin/teachers/create");
  };

  const openEditForm = (teacherId) => {
    setSelectedTeacherId(teacherId);
    setIsFormOpen(true);
  };

  const handleDeleteTeacher = async (teacher) => {
    if (!window.confirm(`Delete ${teacher.name}? This will deactivate the teacher record and remove it from this directory.`)) return;

    setDeletingTeacherId(teacher.id);
    try {
      await api.delete(API_PATHS.admin.teacherById(teacher.id));
      setTeachers((current) => current.filter((item) => item.id !== teacher.id));
      setSelectedTeacherId((current) => current === teacher.id ? "" : current);
      invalidateCache(getTenantCacheKey("admin:teachers"));
      toast.success("Teacher deleted.");
    } catch (error) {
      toast.error(error?.message || "Unable to delete teacher.");
    } finally {
      setDeletingTeacherId("");
    }
  };

  const handleResendInvitation = async (teacher) => {
    if (!teacher.canResendInvitation || resendingInvitationId) return;

    setResendingInvitationId(teacher.id);
    setOpenActionsFor("");
    try {
      const response = await api.post(API_PATHS.admin.resendTeacherInvitation(teacher.id));
      const payload = response?.data?.data ?? response?.data ?? {};
      const accountStatus = String(payload.accountStatus || payload.status || teacher.accountStatus).toUpperCase();
      const canResendInvitation = payload.canResendInvitation === undefined
        ? teacher.canResendInvitation
        : payload.canResendInvitation === true || String(payload.canResendInvitation).toLowerCase() === "true";
      setTeachers((current) => current.map((item) => (
        item.id === teacher.id ? { ...item, accountStatus, canResendInvitation } : item
      )));
      invalidateCache(getTenantCacheKey("admin:teachers"));

      const deliverySucceeded = payload.emailDelivered ?? payload.deliverySuccess ?? payload.emailDeliveryStatus === "SENT";
      if (deliverySucceeded === false) {
        toast.warning("A new invitation was created, but email delivery failed. Please check the delivery logs.");
      } else {
        toast.success(`A new invitation was sent to ${teacher.email || teacher.name}.`);
      }
    } catch (error) {
      const status = Number(error?.status || error?.response?.status || 0);
      const fallback = status === 429
        ? "Please wait one minute before resending this invitation."
        : "Unable to resend the invitation.";
      toast.error(error?.message || error?.response?.data?.message || fallback);
    } finally {
      setResendingInvitationId("");
    }
  };

  const persistTeacherLocally = (teacherRecord) => {
    setTeachers((current) => {
      const exists = current.some((teacher) => teacher.id === teacherRecord.id);
      if (!exists) {
        return [teacherRecord, ...current];
      }

      return current.map((teacher) => (teacher.id === teacherRecord.id ? teacherRecord : teacher));
    });
    setSelectedTeacherId(teacherRecord.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(),
      qualification: form.qualification.trim(),
      experience: Number(form.experience || 0),
      contact: form.contact.trim(),
      assignedClass: form.assignedClass.trim(),
      assignedSection: form.assignedSection.trim(),
      monthlySalary: Number(form.monthlySalary || 0),
    };

    const isEditing = Boolean(form.id);

    try {
      const response = isEditing
        ? await api.put(API_PATHS.admin.teacherById(form.id), payload)
        : await api.post(API_PATHS.admin.teachers(), payload);

      const savedTeacher = normalizeTeacher(
        {
          ...(response.data?.data ?? response.data ?? {}),
          ...payload,
          id: isEditing ? form.id : undefined,
          status: isEditing ? selectedTeacher?.status || "ACTIVE" : "PASSWORD_RESET_REQUIRED",
        },
        teachers.length
      );

      persistTeacherLocally(savedTeacher);
      invalidateCache(getTenantCacheKey("admin:teachers"));
      setIsFormOpen(false);
      toast.success(isEditing ? "Teacher updated." : "Teacher created.");
    } catch {
      setIsFormOpen(false);
      toast.error("Failed to save teacher record. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const panelClass = isDarkMode
    ? "border-slate-800 bg-slate-900 shadow-black/20"
    : "border-slate-200 bg-white shadow-slate-200/60";

  const openPaymentModal = (teacher) => {
    setPaymentTeacher(teacher);
    setPaymentForm({
      ...defaultPaymentForm,
      amount: String(teacher.monthlySalary || ""),
      accountHolderName: teacher.name || "",
      paidOn: new Date().toISOString().slice(0, 10),
    });
    setIsPaymentOpen(true);
  };

  const handlePaymentFormChange = (key, value) => {
    setPaymentForm((current) => ({ ...current, [key]: value }));
  };

  const handleProcessPayment = (event) => {
    event.preventDefault();

    if (!paymentTeacher) return;
    if (!Number(paymentForm.amount || 0)) {
      toast.error("Enter a valid payment amount.");
      return;
    }

    recordPayrollPayment({
      teacherId: paymentTeacher.id,
      teacherName: paymentTeacher.name,
      teacherEmail: paymentTeacher.email,
      amount: Number(paymentForm.amount || 0),
      paidOn: paymentForm.paidOn,
      method: paymentForm.method,
      referenceId: paymentForm.referenceId,
      accountHolderName: paymentForm.accountHolderName,
      bankName: paymentForm.bankName,
      accountNumber: paymentForm.accountNumber,
      ifscCode: paymentForm.ifscCode,
      upiId: paymentForm.upiId,
      notes: paymentForm.notes,
      processedBy: "Admin",
    });

    setTeachers((current) => current.map((item) => (
      item.id === paymentTeacher.id
        ? { ...item, lastPayment: paymentForm.paidOn }
        : item
    )));

    setIsPaymentOpen(false);
    toast.success(`Payment marked for ${paymentTeacher.name}.`);
  };

  return (
    <div>
      <AdminResponsiveTabs tabs={[{ key: "activity", label: "Teachers" }, { key: "payroll", label: "Payroll" }]} activeKey={activeView} onChange={setActiveView} tone="emerald" isDarkMode={isDarkMode} />

      <div className={`border-x border-b p-6 shadow-lg ${panelClass}`}>
        {activeView === "activity" && (
          <>
            <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h1 className="text-2xl font-bold">Teachers</h1>
                <p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Manage teacher profiles, teaching assignments and account access.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <ModuleRefreshButton onRefresh={() => fetchTeachers({ forceRefresh: true })} />
                <ModuleImportButton moduleKey="teachers" label="Import teachers" />
                <button type="button" onClick={openCreateForm} className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600"><FaPlusCircle /> Add teacher</button>
              </div>
            </div>
            <div className="mb-4 flex flex-col gap-3">
              <div className="relative flex-1">
                <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className={`w-full rounded-2xl border py-3 pl-10 pr-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900"}`}
                  placeholder="Search by teacher name, employee ID, email or mobile..."
                />
              </div>
            </div>

            <div className="mb-3"><AdminMobileViewToggle value={mobileRecordView} onChange={setMobileRecordView} isDarkMode={isDarkMode} /></div>
            {Object.values(filters).some(Boolean) && (
              <div className={`mb-3 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                <span className="text-xs font-semibold text-slate-500">Active filters</span>
                {Object.entries(filters).filter(([, value]) => value).map(([key, value]) => (
                  <button key={key} type="button" onClick={() => setFilters((current) => ({ ...current, [key]: "" }))} className={isDarkMode ? "inline-flex items-center gap-2 rounded-full bg-emerald-950 px-3 py-1.5 text-xs font-bold text-emerald-300" : "inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-800"}>
                    {key === "accountStatus" ? "Account" : key.replace(/([A-Z])/g, " $1")}: {readableStatus(value)}<span aria-hidden="true">×</span>
                  </button>
                ))}
                <button type="button" onClick={() => setFilters({ department: "", subject: "", employmentType: "", status: "", accountStatus: "" })} className="ml-auto text-xs font-semibold text-slate-500 underline-offset-2 hover:text-rose-600 hover:underline">Clear all</button>
              </div>
            )}
            {mobileRecordView === "cards" && (
              <div className="space-y-3 md:hidden">
                {loading ? <p className={`rounded-2xl p-6 text-center text-sm ${isDarkMode ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-500"}`}>Loading teachers...</p> : loadError ? <div className="rounded-2xl p-6 text-center text-sm"><p>Unable to load teachers.</p><button type="button" onClick={fetchTeachers} className="mt-2 font-semibold text-emerald-600">Retry</button></div> : filteredTeachers.length === 0 ? <p className={`rounded-2xl p-6 text-center text-sm ${isDarkMode ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-500"}`}>No teachers match these filters.</p> : paginatedTeachers.map((teacher) => (
                  <article key={teacher.id} className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-start gap-3"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{initials(teacher.name)}</span><div className="min-w-0 flex-1"><h3 className="truncate font-bold">{teacher.name}</h3></div><span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">{readableStatus(teacher.employmentStatus)}</span></div>
                    <div className={`mt-3 grid grid-cols-2 gap-2 rounded-xl p-3 text-xs ${isDarkMode ? "bg-slate-900" : "bg-slate-50"}`}><div><p className="text-slate-400">Employee ID</p><p className="mt-1 font-mono font-semibold">{teacher.employeeId}</p></div><div><p className="text-slate-400">Department</p><p className="mt-1 font-semibold">{teacher.department}</p></div><div><p className="text-slate-400">Teaching</p><p className="mt-1 font-semibold">{teacher.subject}</p></div><div><p className="text-slate-400">Joined</p><p className="mt-1 font-semibold">{formatDate(teacher.joiningDate)}</p></div></div>
                    <div className="mt-3 grid grid-cols-2 gap-2"><button type="button" onClick={() => openEditForm(teacher.id)} className="inline-flex items-center justify-center rounded-xl border border-emerald-300 px-3 py-2 text-xs font-bold text-emerald-700">Update teacher</button>{teacher.canResendInvitation && <button type="button" disabled={resendingInvitationId === teacher.id} onClick={() => handleResendInvitation(teacher)} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-blue-300 px-3 py-2 text-xs font-bold text-blue-700 disabled:opacity-60"><FaPaperPlane />{resendingInvitationId === teacher.id ? "Sending..." : "Resend invite"}</button>}<button type="button" disabled={deletingTeacherId === teacher.id} onClick={() => handleDeleteTeacher(teacher)} className="inline-flex items-center justify-center rounded-xl border border-rose-300 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-60">{deletingTeacherId === teacher.id ? "Deleting..." : "Delete"}</button></div>
                  </article>
                ))}
              </div>
            )}

            <div className={`${mobileRecordView === "cards" ? "hidden md:block" : "block"} overflow-hidden rounded-2xl border shadow-sm ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-[1120px] w-full table-fixed text-sm">
                  <thead className={isDarkMode ? "border-b border-slate-800 bg-slate-900 text-slate-200" : "border-b border-slate-200 bg-white text-slate-800"}>
                    <tr>
                      <th className="w-[22%] border-r border-slate-200 px-5 py-4 text-left text-sm font-bold dark:border-slate-700"><div className="flex items-center justify-between gap-3"><span>Teacher</span><span className={`relative grid h-7 w-7 shrink-0 place-items-center border transition-colors ${filters.employmentType ? "border-emerald-300 bg-emerald-100 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800"}`} title="Filter employment type"><FaFilter className="text-xs" /><select aria-label="Filter by employment type" value={filters.employmentType} onChange={(event) => setFilters((current) => ({ ...current, employmentType: event.target.value }))} className="absolute inset-0 h-full w-full cursor-pointer opacity-0"><option value="">All</option>{filterOptions.employmentTypes.map((option) => <option key={option} value={option}>{readableStatus(option)}</option>)}</select></span></div></th>
                      <th className="w-[16%] border-r border-slate-200 px-5 py-4 text-left text-sm font-bold dark:border-slate-700">Employee ID</th>
                      <th className="w-[14%] border-r border-slate-200 px-4 py-4 text-left text-sm font-bold dark:border-slate-700"><div className="flex items-center justify-between gap-2"><span>Department</span><span className={`relative grid h-7 w-7 shrink-0 place-items-center border transition-colors ${filters.department ? "border-emerald-300 bg-emerald-100 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800"}`} title="Filter department"><FaFilter className="text-xs" /><select aria-label="Filter by department" value={filters.department} onChange={(event) => setFilters((current) => ({ ...current, department: event.target.value }))} className="absolute inset-0 h-full w-full cursor-pointer opacity-0"><option value="">All</option>{filterOptions.departments.map((option) => <option key={option} value={option}>{option}</option>)}</select></span></div></th>
                      <th className="w-[14%] border-r border-slate-200 px-4 py-4 text-left text-sm font-bold dark:border-slate-700"><div className="flex items-center justify-between gap-2"><span>Teaching</span><span className={`relative grid h-7 w-7 shrink-0 place-items-center border transition-colors ${filters.subject ? "border-emerald-300 bg-emerald-100 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800"}`} title="Filter teaching subject"><FaFilter className="text-xs" /><select aria-label="Filter by subject" value={filters.subject} onChange={(event) => setFilters((current) => ({ ...current, subject: event.target.value }))} className="absolute inset-0 h-full w-full cursor-pointer opacity-0"><option value="">All</option>{filterOptions.subjects.map((option) => <option key={option} value={option}>{option}</option>)}</select></span></div></th>
                      <th className="w-[11%] border-r border-slate-200 px-4 py-4 text-left text-sm font-bold dark:border-slate-700"><div className="flex items-center justify-between gap-2"><span>Status</span><span className={`relative grid h-7 w-7 shrink-0 place-items-center border transition-colors ${filters.status ? "border-emerald-300 bg-emerald-100 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800"}`} title="Filter status"><FaFilter className="text-xs" /><select aria-label="Filter by status" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} className="absolute inset-0 h-full w-full cursor-pointer opacity-0"><option value="">All</option>{["ACTIVE", "ON_LEAVE", "SUSPENDED", "INACTIVE", "RESIGNED"].map((option) => <option key={option} value={option}>{readableStatus(option)}</option>)}</select></span></div></th>
                      <th className="w-[11%] border-r border-slate-200 px-4 py-4 text-left text-sm font-bold dark:border-slate-700"><div className="flex items-center justify-between gap-2"><span>Account</span><span className={`relative grid h-7 w-7 shrink-0 place-items-center border transition-colors ${filters.accountStatus ? "border-emerald-300 bg-emerald-100 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800"}`} title="Filter account status"><FaFilter className="text-xs" /><select aria-label="Filter by account status" value={filters.accountStatus} onChange={(event) => setFilters((current) => ({ ...current, accountStatus: event.target.value }))} className="absolute inset-0 h-full w-full cursor-pointer opacity-0"><option value="">All</option>{["ACTIVE", "PASSWORD_RESET_REQUIRED", "INVITATION_PENDING", "LOCKED", "DISABLED"].map((option) => <option key={option} value={option}>{readableStatus(option)}</option>)}</select></span></div></th>
                      <th className="w-[9%] border-r border-slate-200 px-4 py-4 text-left text-sm font-bold dark:border-slate-700">Joining date</th>
                      <th className="w-[4%] px-4 py-3 text-right font-semibold"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-5"><div className="space-y-3">{[1, 2, 3, 4, 5].map((row) => <div key={row} className={`h-10 animate-pulse rounded ${isDarkMode ? "bg-slate-800" : "bg-slate-100"}`} />)}</div></td>
                      </tr>
                    ) : loadError ? (
                      <tr><td colSpan="8" className="px-4 py-12 text-center"><p className="font-semibold">Unable to load teachers.</p><p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Please try again.</p><button type="button" onClick={fetchTeachers} className="mt-3 text-sm font-semibold text-emerald-600 hover:underline">Retry</button></td></tr>
                    ) : filteredTeachers.length === 0 ? (
                      <tr><td colSpan="8" className="px-4 py-12 text-center"><p className="font-semibold">{teachers.length ? "No teachers match these filters." : "No teachers added yet"}</p><p className={`mt-1 text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{teachers.length ? "Try clearing or changing your filters." : "Create your first teacher to start assigning classes and subjects."}</p>{!teachers.length && <button type="button" onClick={openCreateForm} className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white"><FaPlusCircle /> Add Teacher</button>}</td></tr>
                    ) : paginatedTeachers.map((teacher) => (
                      <tr
                        key={teacher.id}
                        className={`border-t transition-colors ${isDarkMode ? "border-slate-800 hover:bg-slate-900" : "border-slate-100 hover:bg-emerald-50/40"}`}
                      >
                        <td className="px-5 py-4"><div className="flex items-center gap-3">{teacher.photo ? <img src={teacher.photo} alt="" className="h-10 w-10 rounded-xl object-cover" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-xs font-bold text-emerald-700">{initials(teacher.name)}</span>}<div className="min-w-0"><p className="truncate font-semibold">{teacher.name}</p></div></div></td><td className="px-5 py-4 font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">{teacher.employeeId}</td><td className="px-5 py-4 font-medium">{teacher.department}</td><td className="px-5 py-4"><p className="font-medium">{teacher.subject}</p><p className="mt-0.5 text-xs text-slate-500">{teacher.assignedClass === "-" ? "Not assigned" : `${teacher.assignedClass}${teacher.assignedSection === "-" ? "" : teacher.assignedSection}`}</p></td>
                        <td className="px-4 py-3"><span className="inline-flex whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">● {readableStatus(teacher.employmentStatus)}</span></td><td className="px-4 py-3"><span className="inline-flex whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-700">● {readableStatus(teacher.accountStatus)}</span></td><td className="px-4 py-3">{formatDate(teacher.joiningDate)}</td>
                        <td className="relative px-4 py-3 text-right"><button type="button" aria-label={`Actions for ${teacher.name}`} onClick={() => setOpenActionsFor((current) => current === teacher.id ? "" : teacher.id)} className="rounded-lg p-2 hover:bg-slate-100"><FaEllipsisV /></button>{openActionsFor === teacher.id && <div className={`absolute right-4 z-10 mt-1 w-44 rounded-xl border p-1 text-left shadow-lg ${isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}><button type="button" onClick={() => { setOpenActionsFor(""); openEditForm(teacher.id); }} className="block w-full rounded-lg px-3 py-2 text-sm hover:bg-slate-100">View / Edit Teacher</button><button type="button" onClick={() => { setOpenActionsFor(""); toast.info("Schedule remains available in the existing timetable module."); }} className="block w-full rounded-lg px-3 py-2 text-sm hover:bg-slate-100">View Schedule</button>{teacher.canResendInvitation && <button type="button" disabled={resendingInvitationId === teacher.id} onClick={() => handleResendInvitation(teacher)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-60"><FaPaperPlane />{resendingInvitationId === teacher.id ? "Sending..." : "Resend Invitation"}</button>}<button type="button" disabled={deletingTeacherId === teacher.id} onClick={() => { setOpenActionsFor(""); handleDeleteTeacher(teacher); }} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-60">{deletingTeacherId === teacher.id ? "Deleting..." : "Delete teacher"}</button></div>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {!loading && !loadError && filteredTeachers.length > 0 && <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm"><span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredTeachers.length)} of {filteredTeachers.length} teachers</span><div className="flex items-center gap-2"><button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)} className="rounded-lg border px-3 py-1.5 disabled:opacity-40">Previous</button><span className="text-xs font-semibold">{page} / {totalPages}</span><button type="button" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)} className="rounded-lg border px-3 py-1.5 disabled:opacity-40">Next</button></div></div>}
          </>
        )}

        {activeView === "payroll" && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Total teachers", value: payrollSummary.totalTeachers },
                { label: "Monthly payroll", value: formatSalary(payrollSummary.totalPayroll) },
                { label: "Average salary", value: formatSalary(payrollSummary.averagePayroll) },
                { label: "Pending reset", value: payrollSummary.pendingResets },
              ].map((item) => (
                <div key={item.label} className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <p className="mt-2 text-2xl font-bold">{item.value}</p>
                </div>
              ))}
            </div>

            <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Teacher</th>
                      <th className="px-4 py-3 text-left font-semibold">Class</th>
                      <th className="px-4 py-3 text-left font-semibold">Monthly salary</th>
                      <th className="px-4 py-3 text-left font-semibold">Last payment</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                      <th className="px-4 py-3 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className={`px-4 py-10 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          Loading payroll records...
                        </td>
                      </tr>
                    ) : filteredTeachers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className={`px-4 py-10 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                          No payroll records found.
                        </td>
                      </tr>
                    ) : filteredTeachers.map((teacher) => (
                      <tr key={teacher.id} className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                        <td className="px-4 py-3">
                          <p className="font-semibold">{teacher.name}</p>
                          <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{teacher.subject}</p>
                        </td>
                        <td className="px-4 py-3">Class {teacher.assignedClass}{teacher.assignedSection !== "-" ? teacher.assignedSection : ""}</td>
                        <td className="px-4 py-3 font-semibold">{formatSalary(teacher.monthlySalary)}</td>
                        <td className="px-4 py-3">{teacher.lastPayment || "-"}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${teacher.status === "PASSWORD_RESET_REQUIRED" ? (isDarkMode ? "border-amber-900/60 bg-amber-950/40 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-700") : (isDarkMode ? "border-emerald-900/60 bg-emerald-950/40 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700")}`}>
                            {teacher.status.replaceAll("_", " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openPaymentModal(teacher)}
                            className="rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600"
                          >
                            Process payment
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <AdminModalShell isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} isDarkMode={isDarkMode}>
        <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Teacher form</p>
              <h2 className="mt-2 text-2xl font-bold">{form.id ? "Update teacher" : "Create teacher"}</h2>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Teacher name</label>
                <input type="text" value={form.name} onChange={(event) => handleFormChange("name", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Enter teacher name" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Email</label>
                <input type="email" value={form.email} onChange={(event) => handleFormChange("email", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="teacher@school.com" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Subject</label>
                <select value={form.subject} onChange={(event) => handleFormChange("subject", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required>
                  <option value="">Select subject</option>
                  {form.subject && !tenantSubjects.some((subject) => subject.name === form.subject) && <option value={form.subject}>{form.subject}</option>}
                  {tenantSubjects.map((subject) => <option key={subject.id || subject.name} value={subject.name}>{subject.name}</option>)}
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Qualification</label>
                <input type="text" value={form.qualification} onChange={(event) => handleFormChange("qualification", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="M.Sc, B.Ed" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Experience (years)</label>
                <input type="number" min="0" value={form.experience} onChange={(event) => handleFormChange("experience", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="5" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Contact number</label>
                <input type="text" value={form.contact} onChange={(event) => handleFormChange("contact", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="+91 9876543210" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Assigned class</label>
                <select value={form.assignedClass} onChange={(event) => { handleFormChange("assignedClass", event.target.value); handleFormChange("assignedSection", ""); }} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required>
                  <option value="">Select class</option>
                  {form.assignedClass && !tenantClasses.some((classRecord) => classRecord.name === form.assignedClass) && <option value={form.assignedClass}>{form.assignedClass}</option>}
                  {tenantClasses.map((classRecord) => <option key={classRecord.id || classRecord.name} value={classRecord.name}>{classRecord.name}</option>)}
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Assigned section</label>
                <select value={form.assignedSection} onChange={(event) => handleFormChange("assignedSection", event.target.value)} disabled={!selectedTenantClass} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required>
                  <option value="">{selectedTenantClass ? "Select section" : "Select class first"}</option>
                  {form.assignedSection && !selectedTenantClass?.sections.some((section) => section.name === form.assignedSection) && <option value={form.assignedSection}>{form.assignedSection}</option>}
                  {(selectedTenantClass?.sections || []).map((section) => <option key={section.id || section.name} value={section.name}>{section.name}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Monthly salary</label>
              <input type="number" min="0" value={form.monthlySalary} onChange={(event) => handleFormChange("monthlySalary", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="40000" required />
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={saving} className="inline-flex items-center rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-70">
                {saving ? "Saving..." : form.id ? "Update teacher" : "Create teacher"}
              </button>
              <button type="button" onClick={() => setIsFormOpen(false)} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </AdminModalShell>

      <AdminModalShell isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} isDarkMode={isDarkMode}>
        <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Payroll action</p>
              <h2 className="mt-2 text-2xl font-bold">Process teacher payment</h2>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
              Admin only
            </span>
          </div>

          {paymentTeacher && (
            <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-200" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
              <p><span className="font-semibold">Teacher:</span> {paymentTeacher.name}</p>
              <p><span className="font-semibold">Email:</span> {paymentTeacher.email || "-"}</p>
              <p><span className="font-semibold">Default salary:</span> {formatSalary(paymentTeacher.monthlySalary)}</p>
            </div>
          )}

          <form onSubmit={handleProcessPayment} className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Payment method</label>
                <select value={paymentForm.method} onChange={(event) => handlePaymentFormChange("method", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`}>
                  <option value="BANK_TRANSFER">Bank transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="CASH">Cash</option>
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Amount</label>
                <input type="number" min="0" value={paymentForm.amount} onChange={(event) => handlePaymentFormChange("amount", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Paid on</label>
                <input type="date" value={paymentForm.paidOn} onChange={(event) => handlePaymentFormChange("paidOn", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Reference ID</label>
                <input type="text" value={paymentForm.referenceId} onChange={(event) => handlePaymentFormChange("referenceId", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="UTR / Transaction ID" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Account holder</label>
                <input type="text" value={paymentForm.accountHolderName} onChange={(event) => handlePaymentFormChange("accountHolderName", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Bank name</label>
                <input type="text" value={paymentForm.bankName} onChange={(event) => handlePaymentFormChange("bankName", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Account number</label>
                <input type="text" value={paymentForm.accountNumber} onChange={(event) => handlePaymentFormChange("accountNumber", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Masked or full" />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>IFSC / UPI ID</label>
                <input type="text" value={paymentForm.method === "UPI" ? paymentForm.upiId : paymentForm.ifscCode} onChange={(event) => handlePaymentFormChange(paymentForm.method === "UPI" ? "upiId" : "ifscCode", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} />
              </div>
            </div>

            <div>
              <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Notes</label>
              <textarea value={paymentForm.notes} onChange={(event) => handlePaymentFormChange("notes", event.target.value)} rows="3" className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Optional payroll note" />
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="inline-flex items-center rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600">Confirm payment</button>
              <button type="button" onClick={() => setIsPaymentOpen(false)} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Cancel</button>
            </div>
          </form>
        </div>
      </AdminModalShell>
    </div>
  );
};

export default AdminTeacherManagement;
